<template>
  <div class="popup-search" :class="{ pop: hasFocus }" tabindex="0">
    <div class="popup-search__header">
      <div class="popup-search__header-input">
        <SearchInput
          :id="id"
          ref="child"
          :error="{}"
          name="Search"
          @update:hasFocus="focusHandler"
          @update:inputValue="inputHandler"
          @update:blurred="blurHandler"
          @keyEnter="searchHandler"
        />
        <div class="popup-search__dropdown">
          <div v-if="!hasValue">
            <p class="paragraph-note m-t--24">Quick links</p>
            <slot name="quicklinks" :blurHandler="blurHandler" />
          </div>
          <div v-if="hasError">
            <p class="paragraph-note m-t--24">
              Could not complete search at this time
            </p>
            <slot name="quicklinks" :blurHandler="blurHandler" />
          </div>
          <div v-if="!hasError && hasValue">
            <p class="paragraph-note m-t--24">Suggested Searches</p>
            <div
              v-if="suggestedSearchRef.length === 0 && !hasError && hasValue"
            >
              <div class="spacer-container"></div>
              <div class="loader"></div>
              <div class="spacer-container"></div>
            </div>
            <ul class="ul--cta ul--cta-lg m-l--n8">
              <li v-for="link in suggestedSearchRef" :key="link.id">
                <a
                  :href="link.link"
                  class="link link--iconleft"
                  @blur="blurhandler"
                >
                  <i
                    class="icon icon-search icon-color--grey3 icon--xs"
                    aria-hidden="true"
                  ></i>
                  <span>{{ link.title }}</span>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <button
        v-if="hasFocus"
        aria-label="Cancel search"
        class="
          mobile-menu__header-cancel
          button--text
          text--bold
          paragraph-note
        "
        @click="closeDropDownHandler"
      >
        Cancel
      </button>
      <button
        class="button button--lg button--primary popup-search__searchbutton"
        aria-label="Search"
        @click="searchHandler"
      >
        <span class="popup-search__searchbutton-text">Search</span
        ><span class="popup-search__searchbutton-icon"
          ><i class="icon icon-search" aria-hidden="true"></i
        ></span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import SearchInput from '../SearchInput/SearchInput.vue';
import optiClient from '../../services/api';
import { useQuery } from '@tanstack/vue-query';

const props = defineProps({
  quicklinks: {
    type: String,
    default: '',
  },
  searchPageUrl: {
    type: String,
    default: '',
  },
  id: {
    type: String,
    default: Math.random().toString(36).substring(2, 12),
  },
});

const hasFocus = ref(false);
const hasValue = ref(false);
const currentSearchTerm = ref('');
const child = ref(null);
const hasError = ref(false);

const suggestedSearchRef = ref([]);

const { refetch, data, status } = useQuery({
  queryKey: [currentSearchTerm],
  queryFn: () => {
    const queryData = optiClient.search.getApiV1FindGlobalSearchAndFiltering(
      currentSearchTerm.value,
      '',
      1,
      5,
    );
    return queryData;
  },
  enabled: false,
  cacheTime: 0,
  refetchOnWindowFocus: false,
});

const focusHandler = () => {
  hasFocus.value = true;
  window.addEventListener('click', clickHandler);
};

const clickHandler = (e) => {
  const isInDropDown = e.target.closest('.popup-search');

  if (!isInDropDown) {
    hasFocus.value = false;
    window.removeEventListener('click', clickHandler);
  }
};

const blurHandler = (e) => {
  // const newTarget = e.relatedTarget;
  // const isInDropDown = newTarget?.closest('.popup-search');
  // const isSearchButton = newTarget?.classList.contains("popup-search__searchbutton");
  // if (!isInDropDown || isSearchButton) {
  //   hasFocus.value = false;
  //   window.removeEventListener('click', clickHandler);
  // }
};

const closeDropDownHandler = () => {
  child.value.clearInput();
  hasFocus.value = false;
  window.removeEventListener('click', clickHandler);
};

const inputHandler = (value) => {
  hasValue.value = value.length > 0;
  currentSearchTerm.value = value;
  debouncedFunction();
};

const debounce = (func, delay) => {
  let timeoutId;
  return function () {
    const context = this;
    const args = arguments;
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func.apply(context, args);
    }, delay);
  };
};

const searchHandler = () => {
  if (currentSearchTerm.value && props.searchPageUrl !== '') {
    window.location.href = `${props.searchPageUrl}?q=${currentSearchTerm.value}`;
  }
};

const refetchHandler = () => {
  refetch();
};

const debouncedFunction = debounce(refetchHandler, 500);

watch(status, () => {
  if (status?.value === 'success') {
    suggestedSearchRef.value = Array.from(data?.value?.data);
  } else if (status?.value === 'error') {
    hasError.value = true;
  }
});
</script>
